import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeOff, Store, ArrowLeft, ShoppingBag, Phone, KeyRound, CheckCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { formatError } from "../api";
import API from "../api";

export default function AuthPage() {
  const [params] = useSearchParams();
  const initMode = params.get("mode") === "register" ? "register" : "login";

  const [mode, setMode] = useState(initMode);
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot password states
  const [forgotMode, setForgotMode] = useState(false); // "request" | "reset" | false
  const [forgotWa, setForgotWa] = useState("");
  const [forgotToken, setForgotToken] = useState("");
  const [adminWa, setAdminWa] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      if (user.role === "admin") navigate("/dashboard/admin");
      else if (user.role === "vendor") navigate("/dashboard/vendeur");
      else navigate("/");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(whatsapp, password);
        if (u.role === "admin") navigate("/dashboard/admin");
        else if (u.role === "vendor") navigate("/dashboard/vendeur");
        else navigate("/");
      } else {
        if (!name.trim()) { setError("Le nom est requis"); setLoading(false); return; }
        if (!whatsapp.trim() || whatsapp.trim().length < 8) { setError("Entrez un numéro WhatsApp valide (ex: 221XXXXXXXX)"); setLoading(false); return; }
        if (password.length < 8) { setError("Le mot de passe doit faire au moins 8 caractères"); setLoading(false); return; }
        const u = await register(name, whatsapp.trim(), password, "vendor");
        if (u.role === "vendor") navigate("/dashboard/vendeur");
        else navigate("/");
      }
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await API.post("/api/auth/forgot-password", { whatsapp: forgotWa.trim() });
      setGeneratedCode(data.token);
      setAdminWa(data.admin_whatsapp || "33777369462");
      setForgotMode("show_code");
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await API.post("/api/auth/reset-password", { token: resetCode.toUpperCase(), new_password: newPassword });
      setResetSuccess(true);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  // Forgot password flow
  if (forgotMode) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex flex-col items-center justify-center px-4 py-8 pb-24 md:pb-8">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm w-full max-w-sm p-6">
          <div className="text-center mb-5">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <KeyRound size={22} className="text-blue-600" />
            </div>
            <h1 className="text-xl font-black text-[#232F3E]">Mot de passe oublié</h1>
          </div>

          {resetSuccess ? (
            <div className="text-center space-y-3">
              <CheckCircle size={40} className="text-green-500 mx-auto" />
              <p className="font-semibold text-green-700">Mot de passe réinitialisé !</p>
              <button onClick={() => { setForgotMode(false); setResetSuccess(false); setMode("login"); }} className="w-full bg-[#16A34A] text-white font-bold py-2.5 rounded-md text-sm">
                Se connecter
              </button>
            </div>
          ) : forgotMode === "show_code" ? (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-600 mb-2">Votre code de réinitialisation :</p>
                <p className="text-3xl font-black text-[#232F3E] tracking-widest">{generatedCode}</p>
              </div>
              <a
                href={`https://wa.me/${adminWa}?text=${encodeURIComponent(`Bonjour Jokkoleen, je souhaite réinitialiser mon mot de passe. Mon code est : ${generatedCode}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold py-2.5 rounded-md text-sm"
              >
                Envoyer le code à l'admin WhatsApp
              </a>
              <p className="text-xs text-gray-500 text-center">L'admin vous enverra votre nouveau mot de passe temporaire, puis utilisez le formulaire ci-dessous pour changer votre mot de passe avec le code.</p>
              <form onSubmit={handleResetPassword} className="space-y-3 pt-2 border-t border-gray-100">
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1 block">Code reçu de l'admin</label>
                  <input type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value)} placeholder="Ex: A3F9C2" required className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm uppercase outline-none focus:ring-2 focus:ring-[#16A34A]" data-testid="reset-code-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1 block">Nouveau mot de passe</label>
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min. 8 caractères" required minLength={8} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#16A34A]" data-testid="new-password-input" />
                </div>
                {error && <p className="text-red-600 text-xs bg-red-50 rounded p-2">{error}</p>}
                <button type="submit" disabled={loading} className="w-full bg-[#16A34A] text-white font-bold py-2.5 rounded-md text-sm disabled:opacity-60" data-testid="confirm-reset-btn">
                  {loading ? "Vérification..." : "Confirmer le nouveau mot de passe"}
                </button>
              </form>
            </div>
          ) : (
            <form onSubmit={handleForgotRequest} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">Votre numéro WhatsApp</label>
                <div className="relative">
                  <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="tel" value={forgotWa} onChange={(e) => setForgotWa(e.target.value)} placeholder="221XXXXXXXXX" required className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#16A34A]" data-testid="forgot-whatsapp-input" />
                </div>
              </div>
              {error && <p className="text-red-600 text-xs bg-red-50 rounded p-2">{error}</p>}
              <button type="submit" disabled={loading} className="w-full bg-[#16A34A] text-white font-bold py-2.5 rounded-md text-sm disabled:opacity-60" data-testid="forgot-submit-btn">
                {loading ? "Vérification..." : "Obtenir un code de réinitialisation"}
              </button>
            </form>
          )}

          <button onClick={() => { setForgotMode(false); setError(""); }} className="w-full mt-4 text-xs text-gray-500 hover:text-gray-700">
            ← Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col items-center justify-center px-4 py-8 pb-24 md:pb-8">
      <Link to="/" className="flex items-center gap-2 text-[#232F3E] mb-6 self-start sm:self-center" data-testid="back-home-link">
        <ArrowLeft size={16} /> Retour à l'accueil
      </Link>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm w-full max-w-sm p-6">
        {/* Logo + titre vendeur */}
        <div className="text-center mb-5">
          <div className="w-12 h-12 bg-[#16A34A] rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Store size={22} className="text-white" />
          </div>
          <h1 className="text-xl font-black text-[#232F3E]">Espace Vendeur</h1>
          <p className="text-xs text-gray-500 mt-0.5">Jokkoleen · Marketplace du Sénégal</p>
        </div>

        {/* Mode toggle */}
        <div className="flex rounded-md border border-gray-200 mb-5 overflow-hidden">
          <button
            type="button"
            onClick={() => { setMode("login"); setError(""); }}
            className={`flex-1 py-2 text-sm font-semibold transition-colors ${mode === "login" ? "bg-[#16A34A] text-white" : "text-gray-600 hover:bg-gray-50"}`}
            data-testid="login-tab"
          >
            Connexion
          </button>
          <button
            type="button"
            onClick={() => { setMode("register"); setError(""); }}
            className={`flex-1 py-2 text-sm font-semibold transition-colors ${mode === "register" ? "bg-[#16A34A] text-white" : "text-gray-600 hover:bg-gray-50"}`}
            data-testid="register-tab"
          >
            Créer ma boutique
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === "register" && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Nom complet / Nom de la boutique</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Votre nom ou nom de boutique"
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#16A34A] focus:border-transparent outline-none"
                data-testid="name-input"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Numéro WhatsApp</label>
            <div className="relative">
              <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="221XXXXXXXXX"
                required
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#16A34A] focus:border-transparent outline-none"
                data-testid="whatsapp-input"
              />
            </div>
            {mode === "login" && (
              <p className="text-xs text-gray-400 mt-1">Administrateur : utilisez votre adresse email</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Mot de passe</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#16A34A] focus:border-transparent outline-none pr-10"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="show-password-toggle"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {mode === "login" && (
              <button
                type="button"
                onClick={() => { setForgotMode("request"); setError(""); }}
                className="text-xs text-[#16A34A] mt-1 hover:underline float-right"
                data-testid="forgot-password-link"
              >
                Mot de passe oublié ?
              </button>
            )}
          </div>

          {mode === "register" && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded p-2 flex items-start gap-1.5 clear-both">
              <Store size={12} className="mt-0.5 shrink-0 text-amber-600" />
              Votre compte vendeur sera examiné par l'administrateur avant activation.
            </p>
          )}

          {error && (
            <p className="text-red-600 text-xs bg-red-50 rounded p-2 clear-both" data-testid="auth-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#16A34A] text-white font-bold py-3 rounded-md hover:bg-[#166534] transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-sm clear-both"
            data-testid={mode === "login" ? "login-submit-btn" : "register-submit-btn"}
          >
            {loading ? "Chargement..." : mode === "login" ? "Se connecter" : "Créer ma boutique vendeur"}
          </button>
        </form>

        <p className="text-center text-xs text-gray-500 mt-4">
          {mode === "login" ? "Pas encore de boutique ? " : "Déjà un compte vendeur ? "}
          <button
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="text-[#16A34A] font-semibold hover:underline"
            data-testid="auth-switch-btn"
          >
            {mode === "login" ? "Créer ma boutique" : "Se connecter"}
          </button>
        </p>

        {/* Séparateur client */}
        <div className="mt-5 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">
            <ShoppingBag size={11} className="inline mr-1" />
            Vous souhaitez acheter ?{" "}
            <Link to="/catalogue" className="text-[#16A34A] font-semibold hover:underline" data-testid="browse-as-guest">
              Parcourez le catalogue sans compte
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
